import type { PricelistProductChannelPricesQuery } from "@/generated/graphql";
import {
	Button,
	Can,
	DialogHeader,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
	toast,
	useDialogController,
	useStackDialog,
} from "@k3-tech/react-kit";
import { EditIcon, RotateCcwIcon } from "lucide-react";
import { useCallback } from "react";
import { usePricelistProductChannelPrices } from "../usecases/usePricelistProductChannelPrices";
import { usePricelistProductSalesChannelDelete } from "../usecases/usePricelistProductSalesChannelDelete";
import PricelistProductSalesChannelUpsertDialog from "./PricelistProductSalesChannelUpsertDialog";

type ChannelPrice = NonNullable<
	PricelistProductChannelPricesQuery["pricelistProduct"]
>["salesChannelPrices"][number];

// Every channel may carry a row; a custom price of 0 means none was set.
const customPriceOf = (row: ChannelPrice) => {
	const price = row.pricelistProductSalesChannel?.customSellingPrice ?? 0;
	return price > 0 ? price : undefined;
};

export default function PricelistProductSalesChannelTable({
	pricelistProductID,
}: {
	pricelistProductID?: string;
}) {
	const { createDialog, closeDialog } = useStackDialog();
	const { confirm } = useDialogController();
	const { deletePricelistProductSalesChannel } =
		usePricelistProductSalesChannelDelete();
	const { data, load } = usePricelistProductChannelPrices(pricelistProductID);

	const rows = data?.pricelistProduct?.salesChannelPrices ?? [];

	const openUpsertDialog = (row: ChannelPrice) => {
		if (!pricelistProductID) return;
		createDialog({
			template: (
				<>
					<DialogHeader>
						<DialogTitle>Sales Channel</DialogTitle>
					</DialogHeader>
					<PricelistProductSalesChannelUpsertDialog
						pricelistProductID={pricelistProductID}
						salesChannelID={row.salesChannelID}
						channelName={row.salesChannel.salesChannelName}
						autoSellingPrice={row.autoSellingPrice}
						customSellingPrice={customPriceOf(row)}
						pricelistProductSalesChannelID={
							row.pricelistProductSalesChannel?.pricelistProductSalesChannelID
						}
						isActive={row.pricelistProductSalesChannel?.isActive}
						onCancel={() => closeDialog()}
						onSuccess={(message) => {
							load();
							closeDialog();
							toast.success(message);
						}}
					/>
				</>
			),
		});
	};

	const handleReset = useCallback(
		async (row: ChannelPrice) => {
			const customPrice = row.pricelistProductSalesChannel;
			if (!customPrice || customPriceOf(row) === undefined) return;
			const channelName = row.salesChannel.salesChannelName;
			const ok = await confirm({
				// Clearing the custom price hands the channel back to the auto price, not to the pricelist price.
				title: `Reset ${channelName} to the auto price?`,
				description: `${channelName} will sell at ${row.autoSellingPrice.toLocaleString()} again.`,
				confirmText: "Reset",
				cancelText: "Cancel",
			});
			if (!ok) return;
			const result = await deletePricelistProductSalesChannel(
				customPrice.pricelistProductSalesChannelID,
			);
			if (!result.error) {
				toast.success(
					`${channelName} uses the auto price again (${row.autoSellingPrice.toLocaleString()})`,
				);
				load();
			}
		},
		[confirm, deletePricelistProductSalesChannel, load],
	);

	return (
		<div className="overflow-hidden rounded-md border border-border">
			<Table>
				{/* HEADER */}
				<TableHeader className="bg-teal-500">
					<TableRow>
						<TableHead className="text-white">Channel Code</TableHead>
						<TableHead className="text-white">Channel Name</TableHead>
						<TableHead className="text-white text-right">
							Selling Price (Auto)
						</TableHead>
						<TableHead className="text-white text-right">
							Selling Price (Custom)
						</TableHead>
						<TableHead className="text-white">Actions</TableHead>
					</TableRow>
				</TableHeader>

				{/* BODY */}
				<TableBody>
					{rows.map((item, index) => (
						<TableRow
							key={item.salesChannelID}
							className={index === 1 ? "bg-teal-50" : ""}
						>
							<TableCell className="font-medium">
								{item.salesChannel.salesChannelType}
							</TableCell>

							<TableCell>{item.salesChannel.salesChannelName}</TableCell>

							<TableCell className="text-right">
								{item.autoSellingPrice.toLocaleString()}
							</TableCell>

							<TableCell className="text-right">
								{customPriceOf(item)?.toLocaleString() ?? "-"}
							</TableCell>

							<TableCell>
								<div className="flex items-center gap-2">
									<Button
										size="icon"
										variant="default"
										onClick={() => openUpsertDialog(item)}
									>
										<EditIcon size={16} />
									</Button>
									<Can permissions="pricelist.edit">
										{/* Only a custom price can be deleted; without one the channel already uses the auto price. */}
										<Button
											size="icon"
											variant="outline"
											title="Reset to the auto price"
											disabled={customPriceOf(item) === undefined}
											onClick={() => handleReset(item)}
										>
											<RotateCcwIcon size={16} />
										</Button>
									</Can>
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
				{rows.length === 0 && (
					<TableFooter>
						<TableRow>
							<TableCell colSpan={5}>
								<div className="text-center">
									{pricelistProductID
										? "No Data"
										: "Save the pricelist price first to set channel prices"}
								</div>
							</TableCell>
						</TableRow>
					</TableFooter>
				)}
			</Table>
		</div>
	);
}
